import { useCallback, useContext, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { getSecondaryTenantLogo, getTenantLogo } from 'utils/tenantLogos';
import { CUSTOMER_LOGO_QUERY_KEYS, LOGO_URL_STALE_TIME_MS } from 'constants/customerLogo';
import { fetchCurrentCustomerLogo } from './customerLogo.api';

type Options = {
    /** Use the tenant's secondary bundled asset as the fallback (the nav panel footer variant). */
    preferSecondary?: boolean;
};

type CurrentCustomerLogo = {
    /** Ready to drop into an <img src>. Always populated — never an empty string. */
    logoSrc: string;
    /** True when the rendered logo came from the API rather than the bundled tenant asset. */
    isRemoteLogo: boolean;
    /** Stable identity of the remote logo, when there is one. */
    imageName?: string;
    /** Wire to the <img onError> so an expired presigned URL self-heals. */
    onLogoLoadError: () => void;
};

/**
 * Resolves the logo the app shell should render.
 *
 * Gated on `commonConfig.enableLogoCustomization`. Whenever the flag is off, the API has no
 * selection, the request fails, or the browser cannot load the URL, this returns the bundled
 * per-tenant asset — i.e. exactly the behaviour before this feature existed.
 *
 * The presigned `file_url` expires after 15 minutes, so it is refetched inside that window and
 * never persisted. Every call site shares one query key, so the three shell components that use
 * this hook are served by a single request.
 */
export function useCurrentCustomerLogo({ preferSecondary = false }: Options = {}): CurrentCustomerLogo {
    const { appConfig } = useContext(UserContext);
    const isCustomizationEnabled = getConfig('commonConfig', appConfig)?.enableLogoCustomization === true;
    const tenantId = process.env.REACT_APP_TENANT_ID || '';

    const bundledLogo = useMemo(
        () => (preferSecondary ? getSecondaryTenantLogo(tenantId) : undefined) || getTenantLogo(tenantId),
        [preferSecondary, tenantId],
    );

    // The URL whose <img> already failed to load; remembering it stops a refetch loop.
    const [failedUrl, setFailedUrl] = useState<string | null>(null);

    const { data, refetch } = useQuery({
        queryKey: CUSTOMER_LOGO_QUERY_KEYS.current,
        queryFn: fetchCurrentCustomerLogo,
        enabled: isCustomizationEnabled,
        staleTime: LOGO_URL_STALE_TIME_MS,
        refetchOnWindowFocus: true,
        retry: 1,
    });

    const remoteUrl = data?.file_url;
    const canUseRemote = Boolean(isCustomizationEnabled && remoteUrl && remoteUrl !== failedUrl);

    /**
     * A presigned URL that expired mid-session answers 403, so the image fails to paint. Mint a
     * fresh one once; if the replacement also fails we stay on the bundled asset instead of
     * retrying forever.
     */
    const onLogoLoadError = useCallback(() => {
        if (!remoteUrl || remoteUrl === failedUrl) return;
        setFailedUrl(remoteUrl);
        refetch();
    }, [remoteUrl, failedUrl, refetch]);

    return {
        logoSrc: canUseRemote && remoteUrl ? remoteUrl : bundledLogo,
        isRemoteLogo: canUseRemote,
        imageName: data?.image_name,
        onLogoLoadError,
    };
}
